import { Document, Page, Text, View } from '@react-pdf/renderer';
import { getAllActiveInvoiceCustomFields } from 'wisestep-api';

import { useFetch } from '../../../../hooks/useFetch';
import { IInvoicePdf } from '../../types/IInvoicePdf';
import { InvoiceTemplate } from '../InvoiceTemplateForm';

import { Client } from './Client';
import { Computation } from './Computation';
import { Description } from './Description';
import { FreeText } from './FreeText';
import { Information } from './Information';
import { Logo } from './Logo';
import MgInternationalPreview from './MgInternationalPdf';
import { OrganizationBank } from './OrganizationBank';
import { OrganizationEntity } from './OrganizationEntity';
import styles from './Styles';
import { TAndC } from './TAndC';

// Create styles

export const InvoicePdf = ({
  fileUrl,
  workspaceId,
  fetchPreviewDetails,
  raisedDate,
  invoiceCode,
  dueDate,
  candidateList,
  total,
  amountInWord,
  description,
  subTotal,
  totalBeforeTaxation,
  selectedInvoiceTaxFields,
  taxation,
  selectedAdditionalTaxFields,
  adjustment,
  termsAndConditions,
  invoiceCurrency,
  invoiceExtras,
  discount,
  discountToggle,
  tabular,
  showCompensationFeeArr,
  template,
  items,
  jobTitle,
  customFields,
  lineItems,
  showSellRateAndUnits,
  placementType,
}) => (
  <>
    {workspaceId !== '6dedb44c-a764-4d50-ae65-3242d136dae4' && (
      <InvoicePreview
        invoice={{
          invoiceTypeName: fetchPreviewDetails.clientInvoiceTypeName,
          ourEntityLegalIdentifier: fetchPreviewDetails.ourEntityLegalIdentifier,
          ourEntity: {
            Legal_Name: fetchPreviewDetails.ourEntityLegalName,
            Registered_address: fetchPreviewDetails.ourEntityAddress,
            Corporate_address: fetchPreviewDetails.ourEntityCorporateAddress,
            Name: fetchPreviewDetails.ourEntityName,
          },
          ourEntityBank: {
            bankName: fetchPreviewDetails.bankName,
            swiftCode: fetchPreviewDetails.bankSwiftCode,
            branchName: fetchPreviewDetails.bankBranch,
            bankAccountNumber: fetchPreviewDetails.bankAccountNumber,
            iban: fetchPreviewDetails.bankIban,
            bankCode: fetchPreviewDetails.bankCode,
          },
          client: {
            legalName: fetchPreviewDetails.clientLegalName,
            address: fetchPreviewDetails.clientAddress,
            name: fetchPreviewDetails.clientName,
          },
          raisedDate,
          invoiceCode,
          dueDate,
          currency: invoiceCurrency,
          total,
          amountInWord,
          description,
          subTotal,
          taxation,
          adjustment,
          termsAndConditions,
          descriptive: !tabular,
          showCompensationFeeArr,
          extras: fetchPreviewDetails.invoiceExtras,
          discount,
          discountToggle,
          candidateList,
          invoiceExtras,
          totalBeforeTaxation,
          selectedInvoiceTaxFields,
          selectedAdditionalTaxFields,
          items,
          customFields: customFields,
          lineItems,
          showSellRateAndUnits,
          placementType,
        }}
        fileUrl={fileUrl}
        template={template}
      />
    )}
    {workspaceId == '6dedb44c-a764-4d50-ae65-3242d136dae4' && (
      <MgInternationalPreview
        invoice={{
          invoiceTypeName: fetchPreviewDetails.clientInvoiceTypeName,
          ourEntityLegalIdentifier: fetchPreviewDetails.ourEntityLegalIdentifier,
          ourEntity: {
            Legal_Name: fetchPreviewDetails.ourEntityLegalName,
            Registered_address: fetchPreviewDetails.ourEntityAddress,
            Corporate_address: fetchPreviewDetails.ourEntityCorporateAddress,
            Name: fetchPreviewDetails.ourEntityName,
          },
          ourEntityBank: {
            bankName: fetchPreviewDetails.bankName,
            swiftCode: fetchPreviewDetails.bankSwiftCode,
            branchName: fetchPreviewDetails.bankBranch,
            bankAccountNumber: fetchPreviewDetails.bankAccountNumber,
            iban: fetchPreviewDetails.bankIban,
            bankCode: fetchPreviewDetails.bankCode,
          },
          client: {
            legalName: fetchPreviewDetails.clientLegalName,
            address: fetchPreviewDetails.clientAddress,
            name: fetchPreviewDetails.clientName,
          },
          raisedDate,
          invoiceCode,
          dueDate,
          currency: invoiceCurrency,
          total,
          amountInWord,
          description,
          subTotal,
          taxation,
          adjustment,
          termsAndConditions,
          descriptive: !tabular,
          showCompensationFeeArr,
          extras: fetchPreviewDetails.invoiceExtras,
          discount,
          discountToggle,
          candidateList,
          invoiceExtras,
          totalBeforeTaxation,
          selectedInvoiceTaxFields,
          selectedAdditionalTaxFields,
          items,
          jobTitle,
          customFields: customFields,
          lineItems,
          showSellRateAndUnits,
          placementType,
        }}
        fileUrl={fileUrl}
        template={template}
      />
    )}
  </>
);

interface InvoicePreviewProps {
  template?: InvoiceTemplate;
  invoice: IInvoicePdf;
  fileUrl?: string;
}

export const InvoicePreview = ({ template, invoice, fileUrl }: InvoicePreviewProps) => {
  const [customFields] = useFetch(getAllActiveInvoiceCustomFields);
  const tabular = !invoice.descriptive;

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.border}>
          <View style={{ height: '100%', position: 'relative' }}>
            <Text style={styles.title}>{invoice.invoiceTypeName}</Text>
            {!template ? (
              <>
                <View style={styles.invoice_body}>
                  <OrganizationEntity invoice={invoice} />
                  <Logo fileUrl={fileUrl} />
                </View>
                <View style={styles.invoice_body}>
                  <View style={{ width: '40%' }}>
                    <Information invoice={invoice} />
                  </View>
                  <OrganizationBank invoice={invoice} />
                </View>
                <Client invoice={invoice} />
                <Description invoice={invoice} tabular={tabular} />
                <Computation invoice={invoice} customFields={customFields ?? []} />
                <TAndC invoice={invoice} />
                <FreeText />
              </>
            ) : null}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePdf;
